// Must load before any other module: patches window.fetch/AbortController for browsers that
// predate native support (e.g. Safari < 12.1) -- this package.json's browserslist nominally
// targets Safari 10+. Every AbortController use in this app (useApiClient, useEvidencePhotoUpload,
// useGeoLocation, useAcceptJs, ExportService, FinishSignUp) constructs it unconditionally with no
// guard of its own, relying on this single polyfill instead of each call site duplicating a
// fallback.
import "abortcontroller-polyfill/dist/polyfill-patch-fetch";
import React from "react";
import ReactDOM from "react-dom";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { themePrimary } from "@app.automotus.io/components/themes";
import { Auth0ProviderWithHistory } from "@app.automotus.io/components/auth";
import AuthorizedApolloProvider from "@app.automotus.io/components/AuthorizedApolloProvider";
import { AccountContextProvider, AccountContextInitializer } from "@app.automotus.io/components/context/AccountContext";
import App from "@curbsuite/App";
import { EnvProvider, PollIntervalProvider } from "@app.automotus.io/components/context";
import { SnackPackProvider } from "@app.automotus.io/components/context/SnackPack";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import { datadogLogs } from "@datadog/browser-logs";
import ReactGA from "react-ga4";
import { LicenseInfo } from "@mui/x-license-pro";

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_X_LICENSE_KEY || "");

datadogLogs.init({
  clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN || "",
  site: process.env.REACT_APP_DATADOG_SITE,
  forwardErrorsToLogs: true,
  sampleRate: 100,
  service: "app.automotus.io",
  env: process.env.NODE_ENV === "production" ? "prod" : "dev",
});

ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS_ID || "", {
  gaOptions: {
    cookieFlags: "SameSite=None;Secure",
  },
  gtagOptions: {
    cookie_flags: "SameSite=None;Secure",
  },
});

ReactDOM.render(
  <React.StrictMode>
    <EnvProvider>
      <ThemeProvider theme={themePrimary}>
        {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <BrowserRouter>
            <Auth0ProviderWithHistory cacheLocation="localstorage">
              {/*
               * AccountContextProvider must sit ABOVE AuthorizedApolloProvider so that
               * AuthorizedApolloProvider can read contextId via React.useContext(AccountContext)
               * and inject X-Account-Context-Id into every outbound GraphQL request.
               * AccountContextProvider is pure state (no Apollo dep), so this ordering is safe.
               */}
              <AccountContextProvider>
                <AuthorizedApolloProvider>
                  {/*
                   * AccountContextInitializer fires the AvailableAccountContexts query and
                   * validates the stored contextId. It must live inside AuthorizedApolloProvider
                   * to access Apollo Client, and can read AccountContext because the provider
                   * is above it in the tree.
                   */}
                  <AccountContextInitializer />
                  <SnackPackProvider>
                    <PollIntervalProvider>
                      <App />
                    </PollIntervalProvider>
                  </SnackPackProvider>
                </AuthorizedApolloProvider>
              </AccountContextProvider>
            </Auth0ProviderWithHistory>
          </BrowserRouter>
        </LocalizationProvider>
      </ThemeProvider>
    </EnvProvider>
  </React.StrictMode>,
  document.getElementById("root"),
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
